import {
  animate,
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion
} from "motion/react";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type GlowStatus = "running" | "completed" | "error" | "cancelling";

// How long the completed/failed colour lingers before fading away.
const RESULT_MS = 3500;
const RUNNING = ["#6366f1", "#22d3ee"];
const COMPLETED = ["#34d399", "#10b981"];
const ERROR = ["#ee4344", "#fb7185"];

// Wraps the children with a slowly rotating gradient border + soft inner glow while deploying.
// On completion the colour crossfades to green, on failure red, eases to a gentle stop, then fades
// out keeping that colour. The rotation only runs while visible, so it costs nothing at rest.
export const ActiveGlow = ({
  status,
  className,
  children
}: {
  status: GlowStatus;
  className?: string;
  children: ReactNode;
}) => {
  const reduced = useReducedMotion();
  const isRunning = status === "running" || status === "cancelling";

  // Hold the result tone for a few seconds once it lands, then release it. Only flourish on a live
  // finish (was deploying, now done) — not when the page is loaded/revisited already in a terminal
  // state, which would otherwise replay the colour every reload or back-navigation.
  const [result, setResult] = useState<"completed" | "error" | null>(null);
  const prevStatus = useRef<GlowStatus | null>(null);
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = status;

    const justFinished =
      (status === "completed" || status === "error") &&
      (prev === "running" || prev === "cancelling");
    if (!justFinished) {
      setResult(null);
      return;
    }
    setResult(status);
    const timer = setTimeout(() => setResult(null), RESULT_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const visible = isRunning || result !== null;

  const turn = useMotionValue(0);
  const c1 = useMotionValue(RUNNING[0]);
  const c2 = useMotionValue(RUNNING[1]);

  // Crossfade the gradient to the active tone. While hidden it keeps its last colour (so it never
  // snaps back to blue mid-fade); it only returns to blue when a fresh run begins.
  useEffect(() => {
    const tone =
      result === "error"
        ? ERROR
        : result === "completed"
          ? COMPLETED
          : isRunning
            ? RUNNING
            : null;
    if (!tone) {
      return;
    }
    const a1 = animate(c1, tone[0], { duration: 0.8, ease: "easeInOut" });
    const a2 = animate(c2, tone[1], { duration: 0.8, ease: "easeInOut" });
    return () => {
      a1.stop();
      a2.stop();
    };
  }, [result, isRunning, c1, c2]);

  // Spin steadily while running; ease to a gentle stop during the result flourish.
  useEffect(() => {
    if (reduced || (!isRunning && !result)) {
      return;
    }
    if (isRunning) {
      const controls = animate(turn, turn.get() + 1, {
        ease: "linear",
        duration: 4,
        repeat: Infinity
      });
      return () => controls.stop();
    }
    const controls = animate(turn, turn.get() + 0.6, {
      ease: "easeOut",
      duration: RESULT_MS / 1000
    });
    return () => controls.stop();
  }, [turn, reduced, isRunning, result]);

  const gradient = useMotionTemplate`conic-gradient(from ${turn}turn, transparent 0deg, ${c1} 60deg, ${c2} 120deg, ${c1} 180deg, transparent 250deg)`;

  return (
    <div className={cn("relative rounded-xl", className)}>
      <div className="relative overflow-hidden rounded-[inherit]">
        {children}
        {/* Ambient inner glow blooming in from the edges. */}
        <motion.div
          aria-hidden="true"
          style={{ backgroundImage: gradient }}
          className={cn(
            "glow-spill-mask pointer-events-none absolute inset-[-10%] z-10 blur-2xl transition-opacity duration-700",
            visible ? "opacity-20" : "opacity-0"
          )}
        />
      </div>

      {/* Crisp gradient border on top of the edge. */}
      <motion.div
        aria-hidden="true"
        style={{ backgroundImage: gradient }}
        className={cn(
          "gradient-ring pointer-events-none absolute inset-0 z-20 rounded-[inherit] transition-opacity duration-700",
          visible ? "opacity-90" : "opacity-0"
        )}
      />
    </div>
  );
};
